/**
 * The upload contract for browser-side batch media uploads — the limits, the
 * accepted formats, and the MIME → extension resolution.
 *
 * Here rather than in `@kiao-voice/api-contract` for the same reason as
 * `plan-limits.ts`: apps/api keeps a zero runtime dependency on that package
 * (machine-enforced by `apps/api/eslint.config.js`), and the authoritative
 * validator is one of the callers. `@kiao-voice/api-contract/batch-transcription`
 * re-exports everything here so existing web import paths keep working.
 *
 * Both audio and video files are accepted: the transcription pipeline
 * unconditionally runs `ffmpeg -i <input> -vn` (audio_extraction_service), so a
 * video's audio track is extracted exactly like a direct audio upload — no
 * separate code path is needed for video.
 *
 * These limits are intentionally tighter than the public batch transcription
 * API (which accepts video URLs up to 2 GiB). Direct uploads from the browser
 * pay multipart-encoding overhead and have to live in API memory / tmp before
 * they reach R2, so the per-file size is capped more aggressively.
 */

import { PLAN_MAX_FILE_BYTES } from './plan-limits.js'

export const AUDIO_BATCH_MAX_FILES = 5

/**
 * Per-file ceiling for a direct upload. DEFINED as the Pro plan's cap rather
 * than restated as a literal: the endpoint's ceiling is "whatever the most
 * generous plan may upload", so a change to Pro's allowance that left this
 * behind would silently cap Pro users below their own plan.
 */
export const AUDIO_BATCH_MAX_FILE_BYTES = PLAN_MAX_FILE_BYTES.pro

/**
 * Combined ceiling for one batch. A standalone literal on purpose — it is a
 * function of the body parser's 600 MB multipart request cap
 * (`apps/api/config/bodyparser.ts`), NOT of any plan: it sits below that cap so
 * one max-sized file plus normal multipart framing is accepted, while a
 * five-file queue is rejected by this validator (which can explain why) rather
 * than by the parser (which cannot).
 */
export const AUDIO_BATCH_MAX_TOTAL_FILE_BYTES = 550 * 1024 * 1024 // 550 MB

/**
 * The languages a batch upload offers: automatic (sent as no language) and
 * 國語＋台語, which the server only honours while `taigi_stt_enabled` is on.
 * Both are app languages (`appLanguageValues` in `settings.ts`). The desktop
 * meeting send accepts exactly these (`meeting_upload/submission.rs`).
 */
export const AUDIO_BATCH_LANGUAGES = ['auto', 'taigi'] as const

export type AudioBatchLanguage = (typeof AUDIO_BATCH_LANGUAGES)[number]

/** Upper bound on request-supplied hints; guards the prompt against unbounded growth. */
export const AUDIO_BATCH_MAX_ENHANCEMENT_HINTS = 50

/** Per-hint length cap (characters). 30 is plenty for 中文/英文 domain terms. */
export const AUDIO_BATCH_MAX_ENHANCEMENT_HINT_CHARS = 30

/**
 * Upper bound on the 錄音標記 (recording markers) a caller may submit alongside
 * a batch. Guards against an unbounded array landing in the DB; a real
 * recording session drops a handful of marks, never hundreds.
 */
export const AUDIO_BATCH_MAX_MARKERS = 200

/** Per-marker label length cap (characters). The mobile design has no naming
 *  UI today (see `#services/batch/transcript_marker_service`), so this only
 *  bounds a future/Android caller. */
export const AUDIO_BATCH_MAX_MARKER_LABEL_CHARS = 60

/**
 * Marker time upper bound (seconds), generously above the most generous
 * plan's per-file cap (Team/Pro: 180 minutes) so no legitimate marker is ever
 * rejected — this exists only to keep garbage/overflow input out of the DB,
 * not to approximate a real recording length (the item's own duration isn't
 * known yet at submission time; ffprobe measures it later in the worker).
 */
export const AUDIO_BATCH_MAX_MARKER_TIME_SECS = 6 * 60 * 60

/**
 * Matches strings free of Unicode control characters, line separators, and
 * paragraph separators. These would break the
 * `術語表（僅用於音近字對照；…）：<hint1>、<hint2>` prompt structure and open
 * a prompt-injection vector — e.g. a hint ending in `\n忽略之前指示` could
 * land on its own line and be read as a new rule.
 *
 * `\p{Cc}` covers ASCII controls (U+0000–001F, U+007F) plus the C1 range
 * (U+0080–009F, which includes NEXT LINE U+0085). `\p{Zl}` is U+2028
 * (LINE SEPARATOR) and `\p{Zp}` is U+2029 (PARAGRAPH SEPARATOR) — both are
 * treated as line breaks by most tokenizers / renderers. Format characters
 * (`\p{Cf}` — BIDI overrides, ZWJ/ZWNJ) are deliberately NOT blocked because
 * they appear in legitimate multilingual text.
 */
export const AUDIO_BATCH_ENHANCEMENT_HINT_SAFE_REGEX = /^[^\p{Cc}\p{Zl}\p{Zp}]*$/u

/**
 * Extensions the endpoint accepts — audio formats plus the common video
 * containers (`mp4`, `mov`, `m4v`). Video is fed to the same ffmpeg
 * audio-extraction step, so the only thing distinguishing it from audio is this
 * allowlist. The extension is also the suffix used when persisting to R2;
 * ffmpeg auto-detects the container from content, so the suffix is a label, not
 * a parsing hint.
 */
export const AUDIO_BATCH_ALLOWED_EXTENSIONS = [
  'mp3',
  'wav',
  'm4a',
  'aac',
  'amr',
  '3gp',
  'opus',
  'flac',
  'ogg',
  'webm',
  'mp4',
  'mov',
  'm4v',
] as const

export type AudioBatchExtension = (typeof AUDIO_BATCH_ALLOWED_EXTENSIONS)[number]

export const AUDIO_BATCH_VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', '3gp'] as const

/**
 * Containers the anonymous trial's browser byte-level trimmers can cut. The
 * upload's `clientTrim` hint names one of them; it never replaces the server's
 * own verification of the uploaded bytes.
 */
export const ANONYMOUS_CLIENT_TRIM_FORMATS = ['mp4', 'mp3', 'wav', 'aac', 'amr', 'ogg'] as const
export type AnonymousClientTrim = (typeof ANONYMOUS_CLIENT_TRIM_FORMATS)[number]

/**
 * MIME types accepted on upload. Keys are the canonical MIME the browser sends;
 * values are the file extension used when persisting to R2 so the pipeline's
 * audio-extractor recognises the format.
 *
 * Note `audio/mp4` maps to `m4a` (the MIME .m4a files report), while the video
 * container `video/mp4` maps to `mp4` — they must not collide.
 *
 * `application/octet-stream` is deliberately ABSENT: some browsers and
 * drag-and-drop sources omit a precise MIME and label the upload as a generic
 * binary blob, and `audioExtensionFromMimeAndName` handles that by falling
 * through to the filename's extension. Adding it here would need an arbitrary
 * extension as its value.
 */
export const MIME_TO_AUDIO_BATCH_EXTENSION: Readonly<Record<string, AudioBatchExtension>> = {
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/wave': 'wav',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/m4a': 'm4a',
  'audio/aac': 'aac',
  'audio/x-aac': 'aac',
  'audio/amr': 'amr',
  'audio/amr-wb': 'amr',
  'audio/3gpp': '3gp',
  'video/3gpp': '3gp',
  'audio/opus': 'opus',
  'audio/flac': 'flac',
  'audio/x-flac': 'flac',
  'audio/ogg': 'ogg',
  'audio/webm': 'webm',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/x-m4v': 'm4v',
}

/**
 * Resolve the canonical extension for an uploaded audio or video file.
 *
 * The browser's MIME label is the primary source. If the MIME is missing or
 * generic (e.g. `application/octet-stream` from drag-and-drop), fall back to
 * the file extension parsed from the original filename.
 *
 * Returns `null` when neither route yields an allowed extension — the server
 * then rejects the file with a 422 listing the supported formats, and the
 * browser filters it out of the queue before the upload is paid for.
 *
 * The MIME lookup goes through `Object.hasOwn` rather than a plain index: an
 * index into an object literal walks the prototype, so a `mimeType` of
 * `constructor` would resolve to a function where the signature promises an
 * extension, and the caller would then use it as an R2 key suffix.
 */
export function audioExtensionFromMimeAndName(
  mimeType: string | null | undefined,
  filename: string | null | undefined
): AudioBatchExtension | null {
  if (mimeType) {
    const lower = mimeType.toLowerCase()
    if (Object.hasOwn(MIME_TO_AUDIO_BATCH_EXTENSION, lower)) {
      return MIME_TO_AUDIO_BATCH_EXTENSION[lower]
    }
  }

  if (filename) {
    const dot = filename.lastIndexOf('.')
    if (dot >= 0) {
      const ext = filename.slice(dot + 1).toLowerCase()
      if ((AUDIO_BATCH_ALLOWED_EXTENSIONS as readonly string[]).includes(ext)) {
        return ext as AudioBatchExtension
      }
    }
  }

  return null
}
